import { useMemo } from 'react'
import styled from 'styled-components'

import { Breadcrumb, Breadcrumbs } from '@syconium/little-miss-figgy/dist/components'
import { fromMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { cookieKeys } from '../../../../app/_config/Cookies.config'
import { useCookies } from '../../../../app/_providers/CookiesProvider.client'
import { useFeatureFlag } from '../../../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { Product, ProductGroup } from '../../../../types/graphql'

import { ProductGroupCategory, breadcrumbData } from './constants'

export function isProductGroupCategory(value: unknown): value is ProductGroupCategory {
  return Object.values(ProductGroupCategory).includes(value as ProductGroupCategory)
}

const Wrapper = styled.div`
  margin: ${props => props.theme.spacing(3, 4)};
  ${fromMd} {
    margin: ${props => props.theme.spacing(2.5, 10)};
  }
`

export const ProductPageBreadcrumbs: React.FC<{
  genderCategory?: ProductGroup['genderCategory']
  category?: Product['category']
}> = ({ genderCategory: genderCategoryProp, category: categoryProp }) => {
  function isCategoryKey(key: string): key is keyof typeof breadcrumbTranslations.category {
    return key in breadcrumbTranslations.category
  }
  function isSupercategoryKey(
    key: string
  ): key is keyof typeof breadcrumbTranslations.supercategory {
    return key in breadcrumbTranslations.supercategory
  }
  function isGenderKey(key: string): key is keyof typeof breadcrumbTranslations.gender {
    return key in breadcrumbTranslations.gender
  }
  const breadcrumbsDecision = useFeatureFlag({
    key: 'pdp-breadcrumbs',
    defaultVariant: 'no-breadcrumbs',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })
  const translations = useTranslation()
  const breadcrumbTranslations = translations.pages.product.breadcrumbs
  const [cookies] = useCookies([cookieKeys.stylePreference.key])
  const navGenderCookie = cookies.navGender

  //Use the gender category prop, but if it's unisex, default to the nav gender cookie.
  const gender = useMemo((): 'men' | 'women' | 'unisex' => {
    if (genderCategoryProp === 'men' || genderCategoryProp === 'women') {
      return genderCategoryProp
    }
    if (categoryProp === ProductGroupCategory.GiftCard) return 'unisex'
    if (navGenderCookie === 'women' || navGenderCookie === 'men') {
      return navGenderCookie
    }

    return 'unisex'
  }, [genderCategoryProp, navGenderCookie, categoryProp])

  const genderCategoryBreadcrumb = breadcrumbData[gender]
  const genderCategoryKey = genderCategoryBreadcrumb?.key
  const genderCategoryText =
    genderCategoryKey && isGenderKey(genderCategoryKey)
      ? breadcrumbTranslations.gender[genderCategoryKey]
      : undefined
  const genderLink = genderCategoryBreadcrumb.collection

  const category = isProductGroupCategory(categoryProp) ? categoryProp : undefined
  const categoryBreadcrumb = category ? breadcrumbData[gender].category[category] : undefined

  const categoryKey = categoryBreadcrumb?.key
  const categoryText =
    categoryKey && isCategoryKey(categoryKey)
      ? breadcrumbTranslations.category[categoryKey]
      : undefined
  const categoryLink = categoryBreadcrumb?.collection

  const supercategoryBreadcrumb = categoryBreadcrumb?.supercategory
  const supercategoryKey = supercategoryBreadcrumb?.key
  const supercategoryText =
    supercategoryKey && isSupercategoryKey(supercategoryKey)
      ? breadcrumbTranslations.supercategory[supercategoryKey]
      : undefined
  const superCategoryLink = supercategoryBreadcrumb?.collection
  return breadcrumbsDecision === 'yes-breadcrumbs' ? (
    <Wrapper>
      <Breadcrumbs aria-label={breadcrumbTranslations.label}>
        {genderLink && genderCategoryText && (
          <Breadcrumb href={genderLink}>{genderCategoryText}</Breadcrumb>
        )}
        {superCategoryLink && supercategoryText && (
          <Breadcrumb href={superCategoryLink}>{supercategoryText}</Breadcrumb>
        )}
        {categoryLink && categoryText && (
          <Breadcrumb href={categoryLink}>{categoryText}</Breadcrumb>
        )}
      </Breadcrumbs>
    </Wrapper>
  ) : null
}
